"use client";

import { Input, InputProps } from "@/components/Input";
import { BuilderBlockProps, CallbackReturnType } from "@/types";
import { Dropdown } from "@/types/General/Dropdown";
import { BuilderContextInterface } from "@builder.io/sdk-react";
import { ChangeEvent, FC, PropsWithChildren, useEffect, useRef } from "react";
import { useBuilderFormsContext } from "@/hooks";

type PhoneInputBlockProps = {
  variant: "form" | "round";
  label?: string;
  invalidText?: string;
  diallingCodes: Dropdown[];
  defaultDiallingCode?: Dropdown;
  phoneNumberValue?: string;
  diallingCodeValue?: Dropdown;
  onPhoneNumberChange?: (
    event: ChangeEvent<HTMLInputElement>,
  ) => CallbackReturnType;
  onDiallingCodeChange?: (value: Dropdown) => CallbackReturnType;
  onPhoneNumberInvalid?: (event: unknown) => CallbackReturnType;
  onPhoneNumberBlur?: (event: unknown) => CallbackReturnType;
  onChangeState?: (
    event: unknown,
    correlationId?: string,
  ) => CallbackReturnType;
  onInitialize?: (elem: HTMLDivElement | null) => () => void;
} & Extract<InputProps, { type: "tel" }> & {
    builderContext: BuilderContextInterface;
  } & BuilderBlockProps;

export const PhoneInputBlock: FC<PropsWithChildren<PhoneInputBlockProps>> = ({
  children: _,
  attributes,
  builderContext,
  defaultDiallingCode,
  phoneNumberValue,
  onPhoneNumberChange,
  onDiallingCodeChange,
  onPhoneNumberInvalid,
  onPhoneNumberBlur,
  onChangeState,
  onInitialize,
  ...props
}) => {
  const { correlationId } = useBuilderFormsContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (onInitialize) {
      onInitialize?.(inputRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (onChangeState) {
      const elem = inputRef.current;
      const onChangeStateWithFormsContext = (event: unknown) => {
        onChangeState(event, correlationId);
      };
      elem?.addEventListener("changeState", onChangeStateWithFormsContext);

      return () => {
        elem?.removeEventListener("changeState", onChangeStateWithFormsContext);
      };
    }
    // correlationId is stable and we want to ensure the effect runs if onChangeState changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onChangeState]);

  return (
    <Input
      {...attributes}
      {...props}
      ref={inputRef}
      value={phoneNumberValue}
      diallingCodeValue={props.diallingCodeValue ?? defaultDiallingCode}
      onChange={onPhoneNumberChange}
      onDiallingCodeChange={onDiallingCodeChange}
      onInvalid={onPhoneNumberInvalid}
      onBlur={onPhoneNumberBlur}
      type="tel"
    />
  );
};
